import React from 'react'
import { useTranslation } from 'react-i18next'
import { FileCheck, ShieldCheck, Lock, Landmark, GraduationCap, Building2, Users2, Workflow, Gauge, BarChart3, ClipboardList, FileText, CheckCircle, Cpu } from "lucide-react"
import { useNavigationStore } from '@/stores/navigation'

const ProbationCX: React.FC = () => {
  const { t } = useTranslation()
  const navigationStore = useNavigationStore()

  const features = [
    { title: t('probationcx.features.items.standardizedDigitalReferrals.title'), body: t('probationcx.features.items.standardizedDigitalReferrals.body'), Icon: ClipboardList },
    { title: t('probationcx.features.items.twoWayVisibility.title'), body: t('probationcx.features.items.twoWayVisibility.body'), Icon: Landmark },
    { title: t('probationcx.features.items.secureInformationExchange.title'), body: t('probationcx.features.items.secureInformationExchange.body'), Icon: Lock },
    { title: t('probationcx.features.items.automatedNotifications.title'), body: t('probationcx.features.items.automatedNotifications.body'), Icon: Users2 },
    { title: t('probationcx.features.items.auditComplianceReporting.title'), body: t('probationcx.features.items.auditComplianceReporting.body'), Icon: FileText },
    { title: t('probationcx.features.items.fasterTimeToService.title'), body: t('probationcx.features.items.fasterTimeToService.body'), Icon: CheckCircle },
  ]

  const useCases = [
    { title: t('probationcx.useCases.items.substanceUseMentalHealth.title'), body: t('probationcx.useCases.items.substanceUseMentalHealth.body'), Icon: Gauge },
    { title: t('probationcx.useCases.items.educationEmployment.title'), body: t('probationcx.useCases.items.educationEmployment.body'), Icon: GraduationCap },
    { title: t('probationcx.useCases.items.reentryTransitionSupport.title'), body: t('probationcx.useCases.items.reentryTransitionSupport.body'), Icon: Building2 },
    { title: t('probationcx.useCases.items.probationCheckIns.title'), body: t('probationcx.useCases.items.probationCheckIns.body'), Icon: Landmark },
  ]

  const whyBullets = [
    { title: t('probationcx.whyProbationcx.items.fromPaperToDigital.title'), details: t('probationcx.whyProbationcx.items.fromPaperToDigital.details', { returnObjects: true }), Icon: FileCheck },
    { title: t('probationcx.whyProbationcx.items.betterOutcomesThroughVisibility.title'), details: t('probationcx.whyProbationcx.items.betterOutcomesThroughVisibility.details', { returnObjects: true }), Icon: Workflow },
    { title: t('probationcx.whyProbationcx.items.builtForJusticeSystems.title'), details: t('probationcx.whyProbationcx.items.builtForJusticeSystems.details', { returnObjects: true }), Icon: Landmark },
    { title: t('probationcx.whyProbationcx.items.fasterAdoptionTraining.title'), details: t('probationcx.whyProbationcx.items.fasterAdoptionTraining.details', { returnObjects: true }), Icon: Users2 },
    { title: t('probationcx.whyProbationcx.items.provenScalability.title'), details: t('probationcx.whyProbationcx.items.provenScalability.details', { returnObjects: true }), Icon: Cpu },
  ]

  const faqs = [
    { q: t('probationcx.faq.items.differentFromExisting.question'), a: t('probationcx.faq.items.differentFromExisting.answer'), Icon: ClipboardList },
    { q: t('probationcx.faq.items.secureEnough.question'), a: t('probationcx.faq.items.secureEnough.answer'), Icon: ShieldCheck },
    { q: t('probationcx.faq.items.implementationTime.question'), a: t('probationcx.faq.items.implementationTime.answer'), Icon: FileCheck },
    { q: t('probationcx.faq.items.pricing.question'), a: t('probationcx.faq.items.pricing.answer'), Icon: BarChart3 },
  ]

  const handleBookDemo = () => {
    navigationStore.openDemoModal()
  }

  return (
    <div className="min-h-screen w-full text-white">
      {/* STICKY MOBILE CTA */}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-white/90 backdrop-blur border-t border-slate-200 p-3 sm:hidden">
        <div className="flex gap-3">
          <button onClick={handleBookDemo} className="flex-1 text-center bg-[#395091] text-white font-semibold px-4 py-3 rounded-lg shadow-md">
            {t('common.bookDemo')}
          </button>
          <a
            href="/use-case-finder?product=probationcx"
            className="hidden w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
          >
            {t("common.downloadCaseStudy")}
          </a>
        </div>
      </div>

      {/* HERO */}
      <main className="bg-white text-slate-800">
        <header className="w-full" style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-white">
            <div className="mb-8 sm:mb-10">
              <h2 className="text-6xl sm:text-7xl md:text-8xl leading-none tracking-tight">
                <span >probation</span>
                <span className="font-bold text-[#7AC943]">CX</span>
                <sup className="align-super text-2xl sm:text-3xl md:text-4xl ml-1">™</sup>
              </h2>
            </div>

            <div className="max-w-3xl">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
                {t('probationcx.hero.title')}
              </h1>
              <p className="text-base sm:text-lg md:text-xl/8 opacity-95">
                {t('probationcx.hero.subtitle')}
              </p>
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <button onClick={handleBookDemo} className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition">
                  {t('common.bookDemo')}
                </button>
                <a
                  href="/use-case-finder?product=probationcx"
                  className="hidden w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
                >
                  {t('common.downloadCaseStudy')}
                </a>
              
              </div>
            </div>
          </div>
        </header>

        {/* Features */}
        <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            {t('probationcx.features.title')}
          </h3>
          <p className="text-slate-600 mb-6">
            {t('probationcx.features.subtitle')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {features.map(({ title, body, Icon }, i) => (
              <div key={i} className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm hover:shadow-md transition bg-white/70 backdrop-blur-[1px]">
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#F0F5FF]">
                    <Icon className="w-5 h-5 text-[#395091]" aria-hidden="true" />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">{title}</h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Use Cases */}
        <section id="use-cases" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 bg-gradient-to-br from-[#EDF7FF] to-[#F9FBFF] rounded-3xl">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 sm:mb-8">
            {t('probationcx.useCases.title')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {useCases.map(({ title, body, Icon }, i) => (
              <div key={i} className="rounded-2xl bg-gradient-to-tr from-[#F0F5FF] to-white border border-slate-200 p-5 sm:p-6 shadow-sm">
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#E3ECFF]">
                    <Icon className="w-5 h-5 text-[#395091]" aria-hidden="true" />
                  </span>
                  <h4 className="font-semibold text-slate-900 text-lg leading-snug">{title}</h4>
                </div>
                <p className="text-slate-600 text-base leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why ProbationCX */}
        <section id="why" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
          <div className="rounded-2xl border border-slate-200 p-5 sm:p-6 shadow-sm bg-gradient-to-br from-[#EDF2FF] via-white to-[#F9FAFB]">
            <h4 className="font-bold text-slate-900 mb-3 text-3xl md:text-4xl">
              {t('probationcx.whyProbationcx.title')}
            </h4>
            <p className="text-slate-700 text-base leading-relaxed mb-4">
              {t('probationcx.whyProbationcx.description')}
            </p>
            <ul className="space-y-3 text-base">
              {whyBullets.map(({ title, details, Icon }, idx) => (
                <li key={idx}>
                  <details className="group">
                    <summary className="cursor-pointer font-medium text-slate-900 flex items-center gap-2">
                      <Icon className="w-5 h-5 text-[#395091]" /> {title}
                    </summary>
                    <div className="mt-2 text-slate-600">
                      <ul className="list-disc ml-5 space-y-1">
                        {Array.isArray(details) && details.map((d, i) => (
                          <li key={i}>{d}</li>
                        ))}
                      </ul>
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Trust & Compliance */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t('probationcx.trust.title')}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">{t('probationcx.trust.hipaa')} / {t('probationcx.trust.cjis')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <FileCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">{t('probationcx.trust.soc2')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Lock className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">{t('probationcx.trust.sso')}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-[#395091]" />
              <span className="text-slate-700 font-medium">{t('probationcx.trust.gdpr')}</span>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 border-t border-slate-200">
          <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            {t('probationcx.faq.title')}
          </h3>
          <ul className="space-y-4">
            {faqs.map(({ q, a, Icon }, i) => (
              <li key={i} className="border border-slate-200 rounded-lg p-4">
                <details className="group">
                  <summary className="flex items-center gap-2 font-medium cursor-pointer text-slate-900">
                    <Icon className="w-5 h-5 text-[#395091]" /> {q}
                  </summary>
                  <p className="mt-2 text-slate-600 leading-relaxed">{a}</p>
                </details>
              </li>
            ))}
          </ul>
        </section>

        {/* Final CTA */}
        <section id="contact" className="relative mt-6 sm:mt-8">
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #395091 0%, #559EBA 100%)" }} />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-14 text-white">
            <div className="grid lg:grid-cols-2 gap-8 sm:gap-10 items-center">
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">
                  {t('probationcx.cta.title')}
                </h3>
                <p className="opacity-90 text-base sm:text-lg">
                  {t('probationcx.cta.subtitle')}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end w-full sm:w-auto">
                <button onClick={handleBookDemo} className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition">
                  {t('common.bookDemo')}
                </button>
              </div>
        </div>
      </div>
        </section>

        {/* Footer note */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 text-slate-500 text-sm">
          {t('probationcx.footer')}
        </section>
      </main>
    </div>
  )
}

export default ProbationCX


